import type { ReactNode } from 'react';

import { DownloadCsvXlsx } from '@/components/molecules/DownloadCsvXlsx';
import type { ExtraExportSheet } from '@/hooks/download-CSV-XLSX';
import type { PDFData } from '@/routes/Client/Location/pages/LocationLiceReport/PdfReport/LocationLicePDFContent';

import { DownloadPDF } from '../DownloadPDF/DownloadPDFButton';

export type ExportColumnObject = {
    name: string;
    header?: string;
};

export type ExportColumnDefinition = string | ExportColumnObject;

export function getExportColumnPath(col: ExportColumnDefinition): string {
    return typeof col === 'string' ? col : col.name;
}

export function getExportColumnHeader(col: ExportColumnDefinition): string {
    if (typeof col === 'string') {
        return col.replace('day.', '');
    }
    return col.header ?? col.name.replace('day.', '');
}

export interface DownloadOptionsProps {
    data: unknown[];
    columns?: ExportColumnDefinition[];
    nameToInclude?: string;
    pdfButtonLocation?: boolean;
    pdfButtonRegion?: boolean;
    clientName?: string;
    /** Shown in PDF header (preferred over weekNumber). */
    reportPeriodLabel?: string;
    weekNumber?: number;
    sheetName?: string;
    extraSheet?: ExtraExportSheet;
    /** Extra export actions rendered alongside the download buttons. */
    children?: ReactNode;
}

function DownloadOptions({
    data,
    columns,
    nameToInclude,
    pdfButtonLocation = false,
    pdfButtonRegion = false,
    clientName,
    reportPeriodLabel,
    weekNumber,
    sheetName,
    extraSheet,
    children,
}: DownloadOptionsProps) {
    const pdfPeriodLabel = reportPeriodLabel ?? (weekNumber != null ? String(weekNumber) : '');

    return (
        <>
            <DownloadCsvXlsx
                label="CSV"
                data={data}
                COLUMNS={columns}
                nameToInclude={nameToInclude}
                sheetName={sheetName}
                extraSheet={extraSheet}
            />
            <DownloadCsvXlsx
                label="XLSX"
                data={data}
                COLUMNS={columns}
                nameToInclude={nameToInclude}
                sheetName={sheetName}
                extraSheet={extraSheet}
            />
            {pdfButtonLocation && (
                <DownloadPDF
                    buttonText={'PDF'}
                    docGenerator={async (palette) => {
                        const { LocationLicePDFContent } = await import(
                            '@/routes/Client/Location/pages/LocationLiceReport/PdfReport/LocationLicePDFContent'
                        );
                        return (
                            <LocationLicePDFContent
                                palette={palette}
                                clientName={clientName ?? ''}
                                allData={data as PDFData[]}
                                weekNumber={pdfPeriodLabel}
                            />
                        );
                    }}
                    fileName={nameToInclude ?? `LiceReport${pdfPeriodLabel}`}
                />
            )}
            {pdfButtonRegion && (
                <DownloadPDF
                    buttonText={'PDF'}
                    docGenerator={async (palette) => {
                        const { RegionLicePDFContent } = await import(
                            '@/routes/Client/Overview/RegionLiceReport/PdfReport/RegionLicePDFContent'
                        );
                        return (
                            <RegionLicePDFContent
                                palette={palette}
                                clientName={clientName ?? ''}
                                allData={data as PDFData[]}
                                weekNumber={pdfPeriodLabel}
                            />
                        );
                    }}
                    fileName={nameToInclude ?? `LiceReport${pdfPeriodLabel}`}
                />
            )}
            {children}
        </>
    );
}

export default DownloadOptions;
export { DownloadOptions };
